/**
 * Configurateur Manager - Moon Moon Agency (moteur générique "par famille")
 *
 * Le Liquid rend TOUT le HTML (étapes + add-ons + mapping variant) et un tableau
 * steps[] auto-descriptif (cle, role, métadonnées). Le JS est AGNOSTIQUE : il ne
 * nomme aucune étape, il lit steps[] et applique un comportement par RÔLE :
 *   - product-selector : compose la clé produit (montage, taille…)
 *   - variant-option   : compose la sous-clé variante (couleur, finition, fil…)
 *   - addon-matched    : add-on filtré par matching (data-match-<cle>)
 *   - quantity         : input numérique + règle dérivée (n-plus-1 | n)
 * Ajouter une famille = 0 ligne de JS (nouvelle config métaobjet). Ajouter un
 * RÔLE = 1 entrée roleHandlers + 1 branche _buildItems.
 *
 * SÉLECTION : des <button aria-pressed>, pas des radios — le design est une
 * suite de lignes « vignette + légende + bouton » (Figma 2398:33432). Le module
 * gère donc lui-même l'exclusivité au sein d'une étape, et la navigation clavier
 * passe par `role="group"` + `aria-labelledby` (pattern mm_product_customizations).
 *
 * ÉTAT VIDE : le parcours démarre vierge (accordéons). Toute lecture d'étape
 * peut donc rendre '' — ce n'est pas une erreur, seulement une étape à faire.
 *
 * PROPRIÉTÉS DE COMMANDE : leurs noms et valeurs viennent des `data-property-*`
 * de l'option choisie, jamais du textContent : elles partent sur la commande du
 * client et ne doivent pas dépendre du markup.
 */

/*********************************************************************************************
 * 🔧 CONFIGURATEUR MANAGER
 */
/* Garde d'idempotence : le fichier peut se retrouver inclus deux fois sur une
   meme page (deux sections le declarant). Sans lui, la seconde inclusion leve
   « Identifier 'ConfigurateurManager' has already been declared » et tout le
   script suivant est ignore. */
(function () {
if (window.ConfigurateurManager) return;

const ConfigurateurManager = {
	config: {
		selectors: {
			root: '[data-configurateur]',
			variantsData: '[data-configurateur-variants]',
			addonHint: '[data-addon-hint]',
			feedback: '[data-configurateur-feedback]',
			/* Une option = un <button aria-pressed> (le design n'a pas de radios).
			   `[data-option]` marque la ligne englobante, masquable d'un bloc. */
			option: '[data-option]',
			optionLabel: '[data-option-label]',
			stepLegend: '[data-step-legend]',
			/* Le CTA du formulaire produit : le configurateur intercepte son submit
			   plutot que d'avoir son propre bouton. Fiche produit, ou formulaire de
			   la carte quand le parcours est rendu dans sa popup (grilles). */
			form: '#product-form, [data-type="add-to-cart-form"]',
			productPageForm: '#product-form',
			priceNode: '[data-money-sample]',
			/* Contenu du panneau Caracteristiques, rendu hors du configurateur
			   (mm-product-accordions) : il suit la variante de bundle. */
			specs: '[data-features-content]',
		},
		classes: {
			loading: 'mm-loading',
			unavailable: 'mm-configurateur__value--unavailable',
			unavailableRow: 'mm-configurateur__option-row--unavailable',
			ctaSoldOut: 'mm-configurateur-cta--sold-out',
		},
	},

	state: { root: null, data: null, steps: [], moneyFormat: '${{amount}}' },

	// Comportement par rôle sur changement d'une étape (cle = l'étape modifiée).
	// Générique : ajouter un rôle = ajouter une entrée ici (+ branche _buildItems).
	roleHandlers: {
		'product-selector'(cle) {
			this._refreshDependentSelectors();
			/* Une valeur fixee change une etape variant-option sans clic : les
			   accessoires apparies sur elle (finition) doivent suivre. */
			if (this._refreshFixedOptions()) this._refreshAddons();
			this._refreshConditionalSteps();
			this._refreshAddonsIfMatched(cle);
			this._refreshNotes();
		},
		'variant-option'(cle) {
			this._refreshConditionalSteps();
			this._refreshAddonsIfMatched(cle);
			this._refreshNotes();
		},
		'addon-matched'() { /* sélection add-on : rien à recalculer en amont */ },
		'quantity'() { /* la saisie repasse par _refreshSummary (prix total) */ },
	},

	/**
	 * Monte UNE instance par racine `[data-configurateur]`. Une page pourrait en
	 * porter plusieurs : chaque instance derive du manager via `Object.create` et
	 * recoit son propre `state`, sans quoi la seconde ecraserait l'etat de la
	 * premiere.
	 */
	init() {
		const roots = window.getElements(this.config.selectors.root);
		if (!roots || !roots.length) return;
		roots.forEach((root) => this._mount(root));
	},

	_mount(root) {
		/* Garde de re-entrance : `shopify:section:load` rejoue init() a chaque
		   edition dans le customizer. Sans ce verrou les listeners delegues
		   s'empilent et un ajout au panier partait en N exemplaires. */
		if (root.dataset.mmConfigBound === '1') return;
		root.dataset.mmConfigBound = '1';

		const instance = Object.create(this);
		/* `touched` : les etapes tranchees, par cle — par un clic du client, ou
		   par une preselection du moteur qui vaut choix (selecteur dependant,
		   valeur fixee). Le « sans option » pre-presse n'en est PAS une : il
		   faut un vrai clic — cf. `_refreshStepProgress`. */
		instance.state = { root, data: null, steps: [], touched: {}, moneyFormat: '${{amount}}' };

		instance.state.data = instance._parseData();
		instance.state.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
		if (!instance.state.data) return;

		instance.state.steps = instance.state.data.steps || [];
		instance._indexSteps();

		instance._bindEvents();
		instance._refreshAll();
		window.log('info', '🔧 [CONFIGURATEUR]', 'Initialized (generic engine)', {
			steps: instance.state.steps.length,
		});
		return instance;
	},

	_parseData() {
		const el = this.state.root.querySelector(this.config.selectors.variantsData);
		if (!el) return null;
		try {
			return JSON.parse(el.textContent.trim());
		} catch (e) {
			window.log('error', '🔧 [CONFIGURATEUR]', 'Data parse failed', { error: e.message });
			return null;
		}
	},

	/**
	 * Index dérivés des steps par rôle — source unique, plus rien en dur.
	 */
	_indexSteps() {
		const byRole = (role) => this.state.steps.filter((s) => s.role === role);
		this._productSelectors = byRole('product-selector').map((s) => s.cle);
		this._variantOptions = byRole('variant-option').map((s) => s.cle);
		this._addonSteps = byRole('addon-matched');
		this._quantitySteps = byRole('quantity');
	},

	/**
	 * Traduction depuis data-i18n-* + substitution {placeholders}.
	 * Les chaines arrivent HTML-echappees (locale + escape) : on decode les
	 * entites (&#39; etc.), le feedback est ecrit en textContent, pas en HTML.
	 */
	_t(key, vars) {
		let raw = this.state.root.dataset['i18n' + key.charAt(0).toUpperCase() + key.slice(1)] || '';
		if (raw.indexOf('&') !== -1) {
			const tmp = document.createElement('textarea');
			tmp.innerHTML = raw;
			raw = tmp.value;
		}
		if (!vars) return raw;
		return raw.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
	},

	_bindEvents() {
		/* Selection : le design est fait de boutons, pas de radios. On gere donc
		   l'exclusivite du groupe a la main (aria-pressed), comme le fait
		   mm_product_customizations. */
		this.state.root.addEventListener('click', (e) => {
			/* Etape verrouillee tant que le montage n'est pas choisi
			   (`_refreshStepProgress`) : ni toggle natif, ni remontee vers
			   DetailsAccordionManager (ecouteur sur document). Le clavier
			   (Entree / Espace sur le summary) passe aussi par ce clic. */
			const verrou = e.target.closest('.mm-configurateur__step--locked > summary');
			if (verrou) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}

			/* Lien « Supprimer l'option » d'une ligne pop-up : ne retire que la
			   variante de SA modale, pas les autres accessoires de l'etape. */
			const rm = e.target.closest('[data-popup-remove]');
			if (rm) { this._removePopupChoice(rm); return; }

			const btn = e.target.closest('[data-opt]');
			if (btn && this.state.root.contains(btn)) {
				this._select(btn);
				return;
			}
			const step = e.target.closest('[data-metre-step]');
			if (step) { this._stepMetre(step); return; }

			/* Stepper d'un add-on quantifiable : il vit dans la ligne, pas dans une
			   etape `quantity` — on ne repasse donc pas par les roleHandlers. */
			const addonStep = e.target.closest('[data-addon-step]');
			if (addonStep) this._stepAddon(addonStep);
		});

		/* L'ouverture/fermeture d'une etape ne passe pas par nous
		   (DetailsAccordionManager) : on se raccroche au `toggle` natif pour
		   masquer le rappel des choix quand le panneau s'ouvre. */
		this.state.root.querySelectorAll('[data-step-keys]').forEach((details) => {
			details.addEventListener('toggle', () => this._refreshStepValues());
		});

		this.state.root.addEventListener('input', (e) => {
			if (e.target.matches('[data-metre-input]')) {
				this._refreshSummary();
			}
		});

		/* Le configurateur vit dans le formulaire produit : on intercepte son
		   submit pour envoyer les N lignes liees au lieu de la seule variante.
		   Une instance rendue HORS du formulaire ne doit ni intercepter le submit
		   ni piloter le prix affiche : les deux appartiennent a la fiche produit,
		   donc au bloc. */
		const form = this._form();
		if (form) {
			form.addEventListener('submit', (e) => {
				if (!this.state.root.isConnected) return;
				e.preventDefault();
				/* Coupe la remontee vers le handler delegue de mm_cart.js (submit
				   sur document) : sans ca il postait le formulaire natif en
				   parallele des lignes du bundle des que notre ajout etait refuse
				   (etape requise) — meme parade que mm_product_customizations. */
				e.stopImmediatePropagation();
				this._handleAdd(form.querySelector('[type="submit"]'));
			});

			/* Quantite de la fiche : le total affiche suit le panier (luminaire ET
			   accessoires multiplies, cf. `_buildItems`). Delegation sur le document :
			   le champ de la barre sticky est hors du formulaire, et recopie sa valeur
			   dans celui du formulaire avant que le `change` ne remonte ici. */
			document.addEventListener('change', (e) => {
				if (!this.state.root.isConnected) return;
				const input = e.target;
				if (!input.matches || !input.matches('[name="quantity"], [data-qtty-input]')) return;
				const owned = form.contains(input) || (this._ownsProductPage() && input.closest('[data-sticky-qtty]'));
				if (owned) this._refreshTotalPrice();
			});

			/* +/- d'une carte (popup du configurateur) : `mm_card_product.js` change
			   la valeur sans emettre `change`. On relit apres son handler. */
			form.addEventListener('click', (e) => {
				if (!e.target.closest('[data-card-qtty-increase], [data-card-qtty-decrease]')) return;
				setTimeout(() => this._refreshTotalPrice(), 0);
			});
		}
	},

	/**
	 * Quantite choisie sur la fiche (ou la carte) : elle multiplie le bundle et
	 * ses accessoires, au panier comme dans le prix affiche.
	 */
	_quantite() {
		const form = this._form();
		const qtyInput = form && form.querySelector('[name="quantity"]');
		return Math.max(1, parseInt((qtyInput || {}).value || '1', 10) || 1);
	},

	/**
	 * Formulaire que pilote cette instance : celui de la fiche (le bloc) ou
	 * celui d'une carte produit (popup). `null` pour une section rendue a cote
	 * a titre de reference : elle ne doit ni intercepter le submit ni piloter
	 * de prix.
	 */
	_form() {
		return this.state.root.closest(this.config.selectors.form);
	},

	/**
	 * Cette instance pilote-t-elle la FICHE produit ? Le panneau
	 * Caracteristiques et la barre sticky n'existent que la.
	 */
	_ownsProductPage() {
		return !!this.state.root.closest(this.config.selectors.productPageForm);
	},

	/**
	 * Applique la selection d'une option et relance le rôle correspondant.
	 * Un seul bouton actif par étape — l'exclusivité est portée par `data-opt`.
	 */
	_select(btn) {
		const cle = btn.dataset.opt;
		if (!cle) return;

		/* Trace du passage du client sur cette etape : c'est ce qui la degrise
		   (`_refreshStepProgress`). Pose ici, sur le clic, et nulle part ailleurs
		   — le moteur coche des valeurs de son cote sans que rien n'ait ete
		   choisi. Un re-clic qui retire un accessoire compte aussi : l'etape a
		   bien ete visitee. */
		this.state.touched[cle] = true;

		const container = btn.closest('[data-role]');
		const role = container ? container.dataset.role : '';

		/* Etape addon OPTIONNELLE : selection MULTIPLE — chaque accessoire
		   s'ajoute ou se retire independamment (chaque ligne porte son propre
		   « Ajouter au luminaire »). « Sans option » (valeur vide) reste exclusif
		   et se dépresse des qu'un accessoire est choisi. Une etape addon
		   OBLIGATOIRE (ex. la patere) reste un choix unique, sans retrait. */
		if (role === 'addon-matched' && btn.value) {
			const stepData = this.state.steps.find((s) => s.cle === cle) || {};
			if (stepData.optionnelle) {
				const sans = container.querySelector(`[data-opt="${cle}"][value=""]`);
				if (btn.getAttribute('aria-pressed') === 'true') {
					btn.setAttribute('aria-pressed', 'false');
					this._resetAddonQty(btn);
					const encore = [...container.querySelectorAll(`[data-opt="${cle}"][aria-pressed="true"]`)].some((b) => b.value);
					if (!encore && sans) sans.setAttribute('aria-pressed', 'true');
				} else {
					/* Les variantes d'une meme modale « Choisir mon modele » sont un
					   seul accessoire (Dimmable OU Non dimmable) : choix unique.
					   Seule une modale interne au parcours compte : dans la popup
					   d'une carte, le configurateur entier vit lui-meme dans un
					   <dialog>, qui ne doit pas rendre l'etape exclusive. */
					const modale = btn.closest('dialog');
					if (modale && this.state.root.contains(modale)) {
						modale.querySelectorAll(`[data-opt="${cle}"][aria-pressed="true"]`).forEach((b) => {
							b.setAttribute('aria-pressed', 'false');
							this._resetAddonQty(b);
						});
					}
					btn.setAttribute('aria-pressed', 'true');
					if (sans) sans.setAttribute('aria-pressed', 'false');
				}
				this._refreshSummary();
				return;
			}
			/* Obligatoire deja choisie : re-clic sans effet (pas de retrait). */
			if (btn.getAttribute('aria-pressed') === 'true') return;
		}

		this.state.root.querySelectorAll(`[data-opt="${cle}"]`).forEach((b) => {
			/* « Garder le luminaire sans options » (valeur vide) passe par ici :
			   les accessoires qu'il depresse rendent aussi leur quantite. */
			if (!btn.value && b.value && b.getAttribute('aria-pressed') === 'true') this._resetAddonQty(b);
			b.setAttribute('aria-pressed', 'false');
		});
		btn.setAttribute('aria-pressed', 'true');
		const handler = this.roleHandlers[role];
		if (handler) handler.call(this, cle);
		this._refreshSummary();

		/* « Garder le luminaire sans options » clot l'etape : on la replie sur
		   son rappel « aucun produit » (demande ADR du 2026-10-07). Seule
		   exception au parti pris « le parcours n'ouvre ni ne referme rien
		   seul » : c'est ici le geste explicite du client. Le clic sur le
		   summary reprend l'animation de DetailsAccordionManager. */
		if (role === 'addon-matched' && !btn.value) {
			const details = btn.closest('details');
			const summary = details && details.open && details.querySelector(':scope > summary');
			if (summary) summary.click();
		}
	},

	/**
	 * Boutons - / + du stepper de cordon : bornés par min/max de l'input.
	 */
	_stepMetre(btn) {
		const wrap = btn.closest('[data-metre-stepper]');
		const input = wrap && wrap.querySelector('[data-metre-input]');
		if (!input) return;

		/* Regler la longueur, c'est visiter l'etape (cf. `_select`). */
		const champ = btn.closest('[data-step]');
		if (champ) this.state.touched[champ.dataset.step] = true;

		const delta = parseInt(btn.dataset.metreStep, 10) || 0;
		/* On ne descend pas sous la longueur standard : elle est comprise. Les
		   bornes viennent telles quelles de l'input : le Liquid a DEJA integre la
		   longueur standard dans `max` (l'ajouter ici la comptait deux fois). */
		const base = parseInt(wrap.dataset.metreBase || '0', 10) || 0;
		const min = Math.max(base, parseInt(input.min, 10) || 0);
		const max = parseInt(input.max, 10) || Number.MAX_SAFE_INTEGER;
		const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + delta));

		input.value = String(next);
		this._refreshSummary();
	},

	/**
	 * QUANTITE D'UN ADD-ON RETIRE : retour a son minimum. Sans ce reset, un
	 * add-on quantifiable monte a 5 puis retire (ou masque par le matching)
	 * gardait sa quantite dans l'input ; en le re-choisissant, le client
	 * repartait a 5 sans l'avoir demande — et c'est cette valeur qui part au
	 * panier (`_getAddonsFor`).
	 */
	_resetAddonQty(btn) {
		const row = btn.closest(this.config.selectors.option);
		const input = row && row.querySelector('[data-addon-qty]');
		if (input) input.value = String(parseInt(input.min, 10) || 1);
	},

	/**
	 * Boutons - / + d'un add-on quantifiable (l'isolateur se pose en plusieurs
	 * exemplaires). Met à jour le total : la quantité en fait partie.
	 */
	_stepAddon(btn) {
		const wrap = btn.closest('[data-addon-stepper]');
		const input = wrap && wrap.querySelector('[data-addon-qty]');
		if (!input) return;

		const delta = parseInt(btn.dataset.addonStep, 10) || 0;
		const min = parseInt(input.min, 10) || 1;
		const max = parseInt(input.max, 10) || Number.MAX_SAFE_INTEGER;
		input.value = String(Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + delta)));

		this._refreshSummary();
	},

	/**
	 * Bouton actif d'une étape (attribut plat data-opt="<cle>").
	 */
	_selected(cle) {
		return this.state.root.querySelector(`[data-opt="${cle}"][aria-pressed="true"]`);
	},

	/**
	 * Valeur de l'option choisie. Chaîne vide si l'étape n'est pas encore faite :
	 * le parcours part d'un état vierge, aucune étape n'est pré-cochée. Toute
	 * lecture peut donc légitimement rendre `''`.
	 */
	_get(cle) {
		const el = this._selected(cle);
		return el ? el.value : '';
	},

	/**
	 * Libellé destiné à la commande (ex: 'B' -> "Suspension"). Lu sur un data-*
	 * et non sur le textContent : ces valeurs partent en line item properties,
	 * elles ne doivent pas dépendre du markup.
	 */
	_optionLabel(cle) {
		const el = this._selected(cle);
		if (!el) return '';
		if (el.dataset.propertyValue) return el.dataset.propertyValue;
		const span = el.querySelector(this.config.selectors.optionLabel);
		return span ? span.textContent.trim() : el.value;
	},

	/**
	 * Nom de propriété de commande d'une étape (ex: "Montage"). Même principe :
	 * porté par la donnée, pas par la légende affichée.
	 */
	_stepLegend(cle) {
		const el = this._selected(cle);
		return el && el.dataset.propertyName ? el.dataset.propertyName : '';
	},

	/**
	 * Nom de propriété de l'accordéon qui contient l'étape (son titre BO, ex.
	 * "Abat-jour"), porté par `data-property-name` sur le <details>.
	 */
	_stepGroupName(cle) {
		const fieldset = this.state.root.querySelector(`[data-step="${cle}"]`);
		const details = fieldset && fieldset.closest('[data-step-keys]');
		return details && details.dataset.propertyName ? details.dataset.propertyName : '';
	},

	_refreshAll() {
		this._refreshDependentSelectors();
		this._refreshFixedOptions();
		this._refreshConditionalSteps();
		this._refreshAddons();
		this._refreshNotes();
		this._refreshSummary();
	},

	/**
	 * Toutes les etapes product-selector sont-elles remplies ? Compte les etapes
	 * REELLEMENT presentes : un parcours a 1 seul selecteur est valide (l'ancien
	 * test `indexOf('/')` supposait au moins 2 etapes et le bloquait).
	 */
	_productSelectorsComplets() {
		return this._productSelectors.every((cle) => this._get(cle) !== '');
	},

	/**
	 * Une etape est-elle visible ? Faux quand visible_si l'a masquee (fieldset
	 * ou accordeon entier). Une etape masquee ne compte ni dans la cle variante
	 * ni dans les proprietes de commande.
	 */
	_stepVisible(cle) {
		const fieldset = this.state.root.querySelector(`[data-step="${cle}"]`);
		if (!fieldset || fieldset.hidden) return false;
		const details = fieldset.closest('[data-step-keys]');
		return !(details && details.hidden);
	},

	/**
	 * Etapes variant-option actuellement visibles — les seules qui composent la
	 * cle variante (ex. Mecanisme 2 masque sur un interrupteur simple : le
	 * Liquid saute de la meme facon les options absentes du produit).
	 */
	_visibleVariantOptions() {
		return this._variantOptions.filter((cle) => this._stepVisible(cle));
	},

	/**
	 * Meme comptage pour les etapes variant-option visibles. Une liste VIDE est
	 * complete : un parcours sans option de variante resout sa variante sur la
	 * cle '' (le Liquid genere une entree a cle vide dans ce cas).
	 */
	_variantOptionsCompletes() {
		return this._visibleVariantOptions().every((cle) => this._get(cle) !== '');
	},

	/**
	 * Clé produit = valeurs des étapes product-selector jointes par "/".
	 */
	_productKey(overrideUpTo) {
		return this._productSelectors
			.slice(0, overrideUpTo === undefined ? this._productSelectors.length : overrideUpTo)
			.map((cle) => this._get(cle)).join('/');
	},

	/**
	 * Etapes variant-option qui composent la cle variante du bundle : les
	 * visibles, moins celles dont le produit n'a pas l'option (`_cles`, pose
	 * par le Liquid). Ex. la finition du bundle noir, figee sur Emaillee.
	 */
	_variantKeySteps(bundle) {
		const visibles = this._visibleVariantOptions();
		if (!bundle || !Array.isArray(bundle._cles)) return visibles;
		return visibles.filter((cle) => bundle._cles.indexOf(cle) !== -1);
	},

	/**
	 * Sous-clé variante = valeurs des étapes de `_variantKeySteps` jointes par
	 * "/" (une étape masquée par visible_si ne pèse pas sur la variante).
	 */
	_variantKey(bundle) {
		return this._variantKeySteps(bundle).map((cle) => this._get(cle)).join('/');
	},

	/**
	 * DISPONIBILITÉ des sélecteurs produit dépendants : pour chaque product-selector
	 * après le 1er, masque les valeurs qui ne complètent aucune clé bundle existante
	 * compte tenu des sélecteurs amont. Bascule auto si la valeur cochée disparaît.
	 * Généralise l'ancien _refreshTailles (montage->taille) à N sélecteurs.
	 */
	_refreshDependentSelectors() {
		const bundles = this.state.data.bundles || {};
		const bundleKeys = Object.keys(bundles);
		for (let i = 1; i < this._productSelectors.length; i += 1) {
			const cle = this._productSelectors[i];
			/* Tant que les étapes amont ne sont pas TOUTES faites, aucun préfixe
			   n'est calculable : on laisse tout visible plutôt que de tout masquer.
			   Test par étape (et non `!prefix`) : avec 3 sélecteurs, "A/" passait
			   pour un préfixe valide alors que le 2e restait vide. */
			const amont = this._productSelectors.slice(0, i);
			if (amont.some((k) => this._get(k) === '')) continue;
			const prefix = this._productKey(i); // valeurs des sélecteurs amont

			let selectedOk = false;
			let firstOk = null;
			let firstDispo = null;
			this.state.root.querySelectorAll(`[data-opt="${cle}"]`).forEach((btn) => {
				const candidate = `${prefix}/${btn.value}`;
				// une valeur est dispo si au moins une clé bundle commence par candidate
				const ok = bundleKeys.some((k) => k === candidate || k.startsWith(candidate + '/'));
				/* Masque la ligne englobante si elle existe, sinon le bouton lui-même
				   (les groupes compacts n'ont pas de ligne). */
				const row = btn.closest(this.config.selectors.option);
				(row || btn).hidden = !ok;
				if (ok && !firstOk) firstOk = btn;
				if (ok && !firstDispo && this._valeurDisponible(cle, btn.value)) firstDispo = btn;
				if (ok && btn.getAttribute('aria-pressed') === 'true') selectedOk = true;
			});
			/* Bascule de preference sur une valeur EN STOCK : presélectionner une
			   valeur epuisee alors qu'une autre est disponible menerait droit au
			   CTA « Indisponible ». */
			if (firstDispo) firstOk = firstDispo;

			/* La valeur choisie n'est plus disponible : on bascule sur la première
			   valide, sans relancer _select (on est déjà dans un cycle de refresh).
			   Cette présélection VAUT CHOIX (`touched`) : le client la voit cochée,
			   lui demander de recliquer dessus n'est pas intuitif (demande ADR du
			   2026-10-07) — l'étape se récapitule et dégrise la suite. */
			if (!selectedOk && firstOk) {
				this.state.root.querySelectorAll(`[data-opt="${cle}"]`).forEach((b) => {
					b.setAttribute('aria-pressed', 'false');
				});
				firstOk.setAttribute('aria-pressed', 'true');
				this.state.touched[cle] = true;
			}
		}
	},

	/**
	 * VALEURS FIXEES par la combinaison (`bundles[cle]._fixe`, segments du code
	 * au-dela des product-selector) : l'etape variant-option n'offre plus que
	 * cette valeur, pressee d'office — ex. le bundle noir, qui n'existe qu'en
	 * Emaillee. Elle part au panier comme un choix normal. Tant que le produit
	 * n'est pas designe, ou sur un bundle sans valeur fixee, tout redevient
	 * proposable. Rend `true` si une valeur a change.
	 *
	 * Comme la presélection des sélecteurs dépendants, la pression d'office vaut
	 * choix (`touched`) : il n'y a rien d'autre à choisir.
	 */
	_refreshFixedOptions() {
		const bundle = this._productSelectorsComplets()
			? (this.state.data.bundles || {})[this._productKey()]
			: null;
		const fixes = (bundle && bundle._fixe) || {};
		let change = false;

		this._variantOptions.forEach((cle) => {
			const fixe = fixes[cle];
			if (fixe) this.state.touched[cle] = true;
			this.state.root.querySelectorAll(`[data-opt="${cle}"]`).forEach((btn) => {
				const row = btn.closest(this.config.selectors.option);
				(row || btn).hidden = !!fixe && btn.value !== fixe;
				if (!fixe) return;
				const presse = btn.value === fixe ? 'true' : 'false';
				if (btn.getAttribute('aria-pressed') !== presse) {
					btn.setAttribute('aria-pressed', presse);
					change = true;
				}
			});
		});
		return change;
	},

	/**
	 * DISPONIBILITE d'une valeur d'etape (product-selector ou variant-option) :
	 * existe-t-il une variante de bundle EN STOCK qui la contient ?
	 *
	 * Seuls les choix des etapes PRECEDENTES contraignent, les suivantes valent
	 * joker — comme le selecteur de variantes natif. Un choix aval (le fil) ne
	 * doit pas griser une valeur amont (la finition) : le client resterait bloque
	 * sans comprendre quelle etape defaire. Ex. Lutece : « Emaillee » est grisee
	 * des qu'aucun fil n'existe en stock avec elle.
	 *
	 * Le stock vient du JSON (`available` de chaque variante de bundle), deja
	 * derive par Shopify du stock des composants du bundle.
	 */
	_valeurDisponible(cle, valeur) {
		const ordre = this.state.steps.map((s) => s.cle);
		const rang = ordre.indexOf(cle);
		const contrainte = (c) => {
			if (c === cle) return valeur;
			return ordre.indexOf(c) < rang ? this._get(c) : '';
		};
		const accepte = (cles, parts) => cles.every((c, i) => {
			const v = contrainte(c);
			return !v || parts[i] === v;
		});

		const bundles = this.state.data.bundles || {};
		return Object.keys(bundles).some((cleProduit) => {
			if (!accepte(this._productSelectors, cleProduit.split('/'))) return false;
			const bundle = bundles[cleProduit];
			/* Valeur fixee par la combinaison (`_fixe`) : ce bundle n'offre que
			   celle-la, il ne compte pas pour une autre valeur de l'etape. */
			const fixe = (bundle._fixe || {})[cle];
			if (fixe && fixe !== valeur) return false;
			const cles = this._variantKeySteps(bundle);
			return Object.keys(bundle).some((cleVariante) => {
				if (cleVariante.startsWith('_')) return false;
				const entree = bundle[cleVariante];
				if (!entree || entree.available === false) return false;
				return accepte(cles, cleVariante.split('/'));
			});
		});
	},

	/**
	 * VALEURS EPUISEES grisees mais cliquables (ticket 1240z0fwgvv, choix ADR) :
	 * meme convention que le selecteur natif (`.mm-product-variant-label--unavailable`).
	 * Le client peut toujours les choisir — le CTA dit alors « Indisponible ».
	 * L'attenuation est doublee d'un libelle masque pour les lecteurs d'ecran
	 * (WCAG 1.4.1), et le CTA suit la variante resolue.
	 */
	_refreshAvailability() {
		const option = this.config.selectors.option;
		const soldOut = this._t('soldOut');
		this._productSelectors.concat(this._variantOptions).forEach((cle) => {
			this.state.root.querySelectorAll(`[data-opt="${cle}"]`).forEach((btn) => {
				if (!btn.value) return;
				const epuise = !this._valeurDisponible(cle, btn.value);
				btn.classList.toggle(this.config.classes.unavailable, epuise);
				const row = btn.closest(option);
				if (row) row.classList.toggle(this.config.classes.unavailableRow, epuise);

				let sr = btn.querySelector('[data-unavailable-label]');
				if (epuise && !sr && soldOut) {
					sr = document.createElement('span');
					sr.className = 'visually-hidden';
					sr.setAttribute('data-unavailable-label', '');
					btn.appendChild(sr);
				}
				if (sr) sr.textContent = epuise ? ` (${soldOut})` : '';
			});
		});
		this._refreshCta();
	},

	/**
	 * CTA du formulaire (fiche + barre sticky, ou pop-up d'une carte) :
	 * desactive et libelle « Indisponible » quand la variante resolue est
	 * epuisee, rendu tel quel sinon. Le libelle d'origine est memorise sur le
	 * bouton pour etre restaure.
	 */
	_refreshCta() {
		const form = this._form();
		if (!form) return;
		const v = this._resolveBundleVariant();
		const epuise = !!v && !v.available;
		const soldOut = this._t('soldOut');

		form.querySelectorAll('[type="submit"]').forEach((btn) => {
			if (epuise) {
				if (btn.dataset.mmCfgLabel === undefined) {
					btn.dataset.mmCfgLabel = btn.textContent.trim();
					btn.dataset.mmCfgAria = btn.getAttribute('aria-label') || '';
				}
				btn.disabled = true;
				btn.classList.add(this.config.classes.ctaSoldOut);
				btn.textContent = soldOut;
				btn.removeAttribute('aria-label');
			} else if (btn.dataset.mmCfgLabel !== undefined) {
				btn.disabled = false;
				btn.classList.remove(this.config.classes.ctaSoldOut);
				btn.textContent = btn.dataset.mmCfgLabel;
				if (btn.dataset.mmCfgAria) btn.setAttribute('aria-label', btn.dataset.mmCfgAria);
				delete btn.dataset.mmCfgLabel;
				delete btn.dataset.mmCfgAria;
			}
		});
	},

	/**
	 * ETAT D'AVANCEMENT DES ETAPES (Figma 2849:162819) : une etape dont aucune
	 * valeur n'est encore choisie est grisee, pour montrer d'un coup d'oeil ou
	 * en est le parcours. Elle reste ouvrable — c'est un reperage visuel, pas un
	 * verrou : le moteur explique deja un panneau vide (`criteres_requis`) et
	 * bloque au panier ce qui manque (`etape_requise`).
	 *
	 * Le parcours ne referme et n'ouvre AUCUN panneau tout seul : le client garde
	 * la main sur ce qu'il deplie (decision du 2026-09-25). C'est ce grisage qui
	 * porte desormais le reperage que l'enchainement automatique assurait.
	 */
	_refreshStepProgress() {
		/* PROGRESSION STRICTE : la premiere etape est toujours active, et chaque
		   suivante ne se degrise qu'une fois la precedente REMPLIE. Une seule
		   avance a la fois — le client voit ou il en est sans avoir a deviner.
		   Les etapes masquees (visible_si) ne comptent pas : elles ne barrent
		   pas la route a celles qui suivent. */
		const etapes = [...this.state.root.querySelectorAll('details[data-step-keys]')]
			.filter((d) => !d.hidden);

		/* VERROU DU MONTAGE (demande ADR du 2026-10-07) : tant que la 1re etape
		   n'est pas tranchee, la suite ne s'ouvre pas — sans montage, l'abat-jour
		   deverserait toutes les tailles de tous les produits. Seule la 1re etape
		   verrouille : ensuite, toutes restent explorables (grisees si besoin). */
		const premiereRemplie = !etapes.length || this._stepFilled(etapes[0]);

		let precedentesRemplies = true;
		etapes.forEach((details, i) => {
			const aFaire = !precedentesRemplies;
			details.classList.toggle('mm-configurateur__step--todo', aFaire);

			const verrouillee = i > 0 && !premiereRemplie;
			details.classList.toggle('mm-configurateur__step--locked', verrouillee);
			const summary = details.querySelector(':scope > summary');
			if (summary && verrouillee) summary.setAttribute('aria-disabled', 'true');
			else if (summary) summary.removeAttribute('aria-disabled');

			/* L'attenuation ne dit rien a un lecteur d'ecran (WCAG 1.4.1) : on
			   double le signal visuel d'un libelle masque. `aria-disabled` est
			   reserve au verrou du montage ci-dessus : une etape seulement grisee
			   reste ouvrable. */
			const etat = details.querySelector('[data-step-state]');
			if (etat) etat.textContent = aFaire ? this._t('etapeAVenir') : '';

			if (precedentesRemplies) precedentesRemplies = this._stepFilled(details);
		});
	},

	/**
	 * Une etape est REMPLIE quand le client y a tranche.
	 *
	 * Deux lectures selon le type d'etape :
	 * - etape de parcours : chaque cle de `data-step-keys` porte une valeur
	 *   tranchee (`state.touched`) — clic du client ou preselection du moteur.
	 * - etape addon (hors `data-step-keys`, optionnelle) : un accessoire pris,
	 *   ou « sans option » pose par un vrai clic.
	 */
	_stepFilled(details) {
		const touchees = this.state.touched || {};
		const cles = (details.dataset.stepKeys || '').split(',').map((k) => k.trim()).filter(Boolean);

		if (cles.length) {
			return cles.every((k) => touchees[k] && this._get(k) !== '');
		}

		return [...details.querySelectorAll('[data-step]')]
			.some((field) => touchees[field.dataset.step]);
	},

	/**
	 * Rappel des choix en bout de ligne d'etape (Figma 3479:135165) : visible
	 * seulement quand l'etape est repliee, l'ouvrir laissant la place aux options.
	 */
	_refreshStepValues() {
		this.state.root.querySelectorAll('[data-step-keys]').forEach((details) => {
			const cible = details.querySelector('[data-step-value]');
			if (!cible) return;

			const libelles = (details.dataset.stepKeys || '').split(',')
				.map((k) => this._optionLabel(k.trim()))
				.filter(Boolean);

			/* La longueur de cordon choisie fait partie du rappel quand l'etape
			   quantity du panneau est proposee (« Or · 6 m »). Elle n'est pas dans
			   data-step-keys : une quantite a toujours une valeur, elle ne doit
			   pas compter comme etape a completer. */
			details.querySelectorAll('[data-step][data-role="quantity"]:not([hidden])').forEach((field) => {
				const input = field.querySelector('[data-metre-input]');
				if (!input) return;
				const unit = field.querySelector('[data-metre-unit]');
				libelles.push(input.value + (unit ? ' ' + unit.textContent.trim() : ''));
			});

			/* Une etape d'accessoires annonce un COMPTE, pas des libelles (Figma
			   3846:139766) : « 2 produits », ou « aucun produit » quand le client
			   garde le luminaire nu. Elle n'est pas dans `data-step-keys` (les
			   optionnelles en sont exclues), donc rien ne l'aurait remplie. */
			details.querySelectorAll('[data-step][data-role="addon-matched"]:not([hidden])').forEach((field) => {
				const cle = field.dataset.step;
				const stepData = this.state.steps.find((s) => s.cle === cle) || {};
				if (!stepData.optionnelle) return;
				const n = this._getAddonsFor(cle).reduce((total, addon) => total + addon.quantity, 0);
				libelles.push(n === 0
					? this._t('addonCountZero')
					: this._t(n === 1 ? 'addonCountOne' : 'addonCountOther', { n }));
			});

			const texte = libelles.join(' · ');
			cible.querySelector('[data-step-value-text]').textContent = texte;
			/* Une etape non tranchee n'annonce rien. On se fie a `_stepFilled`,
			   pas au grisage : une etape accessible mais pas encore ouverte est
			   active, sans rien a rappeler pour autant. */
			cible.hidden = !texte || details.open || !this._stepFilled(details);
		});
	},

	/**
	 * DIMENSION A COTE DU LIBELLE D'ETAPE (« Taille • 10 x 14 cm »), meme tete
	 * que le picker de variantes de la fiche produit.
	 *
	 * Elle vit sur la COMBINAISON (`bundles[cle]._dimensions`) et non sur la
	 * valeur : c'est le produit bundle entier qu'elle decrit, donc propre au
	 * modele de luminaire. Elle s'affiche sur la PREMIERE etape product-selector
	 * a partir de laquelle elle est connue : toutes les combinaisons qui
	 * prolongent les choix faits jusque-la portent la meme. C'est la taille sur
	 * Behor (Montage / Taille / Couleur) : la couleur, choisie apres, ne change
	 * pas la taille physique — la rattacher a la derniere etape l'affichait a
	 * cote de « Couleur ». Les combinaisons sans dimension ne comptent pas.
	 *
	 * La puce de separation est posee ici : le noeud reste dans le DOM meme vide
	 * (cf. `mm-product-configurateur-group`), un gap CSS y laisserait un espace
	 * fantome. Rien a afficher tant que le BO n'a pas renseigne les dimensions.
	 */
	_refreshStepDimensions() {
		const cibles = this.state.root.querySelectorAll('[data-step-dimensions]');
		if (!cibles.length) return;

		const bundles = this.state.data.bundles || {};
		let etape = '';
		let dim = '';
		for (let i = 0; i < this._productSelectors.length; i += 1) {
			const cle = this._productSelectors[i];
			if (this._get(cle) === '') break;
			/* Une etape sans emplacement (montage, liste illustree) passe la main. */
			if (!this.state.root.querySelector(`[data-step="${cle}"] [data-step-dimensions]`)) continue;

			const prefix = this._productKey(i + 1);
			const dims = new Set(Object.keys(bundles)
				.filter((k) => k === prefix || k.startsWith(prefix + '/'))
				.map((k) => bundles[k]._dimensions)
				.filter(Boolean));
			if (dims.size === 1) {
				etape = cle;
				[dim] = dims;
				break;
			}
		}

		cibles.forEach((cible) => {
			const champ = cible.closest('[data-step]');
			const actif = champ && champ.dataset.step === etape;
			cible.textContent = '';
			if (!actif || !dim) return;

			const sep = document.createElement('span');
			sep.className = 'mm-product-variant-option__sep';
			sep.setAttribute('aria-hidden', 'true');
			cible.append(sep, dim);
		});
	},

	/**
	 * Notes contextuelles liees a une valeur choisie (ex. l'applique et sa
	 * patère trouée, Figma 1716:22717) : affichées seulement quand la valeur
	 * est choisie (data-value-note + data-note-step/-value, toute etape).
	 */
	_refreshNotes() {
		this.state.root.querySelectorAll('[data-value-note]').forEach((note) => {
			note.hidden = this._get(note.dataset.noteStep) !== note.dataset.noteValue;
		});
	},

	/**
	 * PREMIER VISUEL DISPONIBLE pour les choix de variante DEJA faits.
	 *
	 * La cle variante joint toutes les etapes variant-option, fil compris. Or le
	 * fil ne change pas l'aspect de l'abat-jour : attendre la cle complete
	 * figeait la vignette jusqu'au dernier clic, en donnant l'impression que
	 * seule cette etape-la pilotait l'image.
	 *
	 * On prend donc la variante exacte si elle est connue et porte un visuel,
	 * sinon la premiere variante compatible avec les segments deja choisis (un
	 * segment vide vaut joker). Choisir « Noir » montre la photo noire sans
	 * attendre le reste.
	 *
	 * `champ` : la donnee de variante cherchee — `_media` (visuels) ou `_specs`
	 * (panneau Caracteristiques). Seules les variantes qui la portent comptent.
	 */
	_variantPartiel(bundle, champ) {
		if (!bundle) return null;

		const porte = (entree) => !!(entree && entree[champ] && entree[champ].length);
		const segments = this._variantKeySteps(bundle).map((cle) => this._get(cle));
		const exacte = bundle[segments.join('/')];
		if (porte(exacte)) {
			return { cle: segments.join('/'), valeur: exacte[champ] };
		}

		/* Aucun choix de variante pose : rien a deviner, les visuels du produit
		   bundle (poses par le Liquid) restent en place. */
		if (!segments.some(Boolean)) return null;

		/* On relache les segments du dernier vers le premier jusqu'a trouver un
		   visuel. Les etapes sont ordonnees du plus structurant au moins : couleur
		   et finition changent l'aspect, le fil non. « Noir / Mate / Lin » sans
		   photo retombe donc sur « Noir / Mate / Or » — la bonne suspension — et
		   non sur l'image generique du bundle. */
		for (let garde = segments.length; garde > 0; garde -= 1) {
			const candidate = Object.keys(bundle).find((cle) => {
				if (cle.startsWith('_')) return false;
				if (!porte(bundle[cle])) return false;
				const parts = cle.split('/');
				return segments.slice(0, garde).every((seg, i) => !seg || parts[i] === seg);
			});
			if (candidate) return { cle: candidate, valeur: bundle[candidate][champ] };
		}

		return null;
	},

	/**
	 * PANNEAU CARACTERISTIQUES : Dimensions, Fabrication et Fiche technique
	 * peuvent etre saisies sur la variante de bundle. Le Liquid expose, pour les
	 * seules variantes qui en portent, le contenu deja rendu du panneau
	 * (`_specs`, meme snippet que la fiche). Meme resolution partielle que les
	 * visuels ; sans variante trouvee, le contenu d'origine (fiche d'accueil)
	 * revient.
	 */
	_refreshSpecs() {
		if (!this._ownsProductPage()) return;
		const cible = document.querySelector(this.config.selectors.specs);
		if (!cible) return;

		if (this._specsInitial === undefined) this._specsInitial = cible.innerHTML;

		const bundle = this._productSelectorsComplets()
			? (this.state.data.bundles || {})[this._productKey()]
			: null;
		const trouve = this._variantPartiel(bundle, '_specs');
		const html = trouve ? trouve.valeur : this._specsInitial;
		if (this._lastSpecs === html) return;

		this._lastSpecs = html;
		cible.innerHTML = html;

		/* Fiche d'accueil sans specs propres : le panneau est rendu vide et son
		   declencheur masque tant qu'aucune variante n'en apporte. */
		const panneau = cible.closest('dialog');
		const lien = panneau && document.querySelector(`[data-drawer-open="${panneau.id}"]`);
		if (lien) lien.hidden = !html.trim();
	},

	/**
	 * VIGNETTE DU PANNEAU ILLUSTRE : c'est elle, et non la galerie, qui montre ce
	 * que le client compose — la galerie garde les photos de la fiche d'accueil
	 * (demande ADR du 2026-10-01). En mobile, le configurateur plein ecran ne
	 * montre d'ailleurs que les vignettes.
	 *
	 * Chaque panneau ne suit que SES etapes, en cascade :
	 * 1. panneau qui designe le produit (une etape product-selector, la taille) :
	 *    la photo de la VARIANTE de bundle choisie (finition, propre au modele de
	 *    luminaire puisque portee par son produit bundle) ;
	 * 2. panneau produit encore : l'image de la COMBINAISON, si le BO en a pose
	 *    une (« vignette de la ligne ») ;
	 * 3. le visuel de la VALEUR choisie dans le panneau (champ `image` du
	 *    metaobjet, ex. Or / Lin du fil) — la plus en aval gagne ;
	 * 4. panneau produit encore : la photo du produit bundle ;
	 * 5. sinon l'image posee par le Liquid (champ `image` de l'etape, sinon
	 *    produit) — on ne la remplace jamais par du vide.
	 *
	 * On reecrit `src`/`srcset` de l'<img> existante plutot que de la recreer :
	 * ses attributs de chargement (sizes, loading, decoding) viennent de
	 * `mm-image-snippet` et n'ont pas a etre rejoues.
	 */
	_refreshPanelThumb() {
		const thumbs = this.state.root.querySelectorAll('[data-panel-thumb]');
		if (!thumbs.length) return;

		let photoVariante = '';
		let photoCombinaison = '';
		let photoProduit = '';
		if (this._productSelectorsComplets()) {
			const bundle = (this.state.data.bundles || {})[this._productKey()];
			const trouve = this._variantPartiel(bundle, '_media');
			photoVariante = trouve ? trouve.valeur[0] : '';
			photoCombinaison = (bundle && bundle._image) || '';
			photoProduit = (bundle && bundle._media && bundle._media[0]) || '';
		}

		thumbs.forEach((thumb) => {
			const img = thumb.querySelector('img');
			const panneau = thumb.closest('details');
			if (!img || !panneau) return;

			/* Un panneau comme « Fil electrique » illustre son propre sujet — le
			   fil : il ne montre jamais le luminaire. */
			const suitLeProduit = !!panneau.querySelector('[data-step][data-role="product-selector"]');

			let photoValeur = '';
			panneau.querySelectorAll('[data-value-image][aria-pressed="true"]').forEach((btn) => {
				const champ = btn.closest('[data-step]');
				if (champ && !champ.hidden) photoValeur = btn.dataset.valueImage;
			});

			const visuel = suitLeProduit
				? photoVariante || photoCombinaison || photoValeur || photoProduit
				: photoValeur;

			/* L'image d'origine est mise de cote au premier remplacement : une
			   variante SANS visuel propre doit la faire revenir, sinon celle de la
			   variante precedente resterait affichee a tort. */
			if (thumb.dataset.thumbInitial === undefined) {
				thumb.dataset.thumbInitial = img.getAttribute('src') || '';
				thumb.dataset.thumbInitialSrcset = img.getAttribute('srcset') || '';
			}

			const cible = visuel || thumb.dataset.thumbInitial;
			if (!cible || img.getAttribute('src') === cible) return;

			img.setAttribute('src', cible);
			if (visuel) {
				img.removeAttribute('srcset');
			} else if (thumb.dataset.thumbInitialSrcset) {
				img.setAttribute('srcset', thumb.dataset.thumbInitialSrcset);
			}
		});
	},

	/**
	 * Étapes conditionnelles : visibilité selon visible_si (data steps[]), quel
	 * que soit le rôle (cordon au mètre, patère pour applique…). Un <details>
	 * dont TOUTES les étapes sont masquées se masque en bloc : une étape
	 * conditionnelle peut porter son propre accordéon.
	 */
	_refreshConditionalSteps() {
		this.state.steps.forEach((step) => {
			const cond = step.visible_si;
			if (!cond) return;
			const fieldset = this.state.root.querySelector(`[data-step="${step.cle}"]`);
			if (!fieldset) return;
			const current = this._get(cond.cle);
			const masquee = (cond.valeurs || []).indexOf(current) === -1;

			/* Une etape qui disparait oublie qu'elle a ete visitee : sans ca, la
			   patere bascule C -> A -> C reviendrait « deja remplie » et ouvrirait
			   la suite du parcours sans que le client ait re-valide son choix
			   (`_stepFilled`). Le panier, lui, etait deja protege par la garde
			   `_stepVisible` de `_getAddonsFor`. */
			if (masquee && !fieldset.hidden) delete this.state.touched[step.cle];

			fieldset.hidden = masquee;
		});

		this.state.root.querySelectorAll('[data-step-keys]').forEach((details) => {
			const fieldsets = [...details.querySelectorAll('[data-step]')];
			if (!fieldsets.length) return;
			details.hidden = fieldsets.every((f) => f.hidden);
		});
	},

	/**
	 * Recalcule le matching si l'étape modifiée (cle) conditionne un add-on.
	 */
	_refreshAddonsIfMatched(cle) {
		const impacts = this._addonSteps.some((s) => (s.matched_on || []).indexOf(cle) !== -1);
		if (impacts) this._refreshAddons();
	},

	/**
	 * MATCHING générique : pour chaque étape addon-matched, affiche les add-ons dont
	 * les data-match-<critere> correspondent aux valeurs des étapes de matched_on.
	 */
	_refreshAddons() {
		this._addonSteps.forEach((step) => {
			const criteria = step.matched_on || [];
			const values = {};
			criteria.forEach((c) => { values[c] = this._get(c); });

			/* Tant qu'un critere de matching n'est pas choisi, on ne peut pas savoir
			   quels add-ons sont compatibles : on n'en propose donc AUCUN, plutot
			   que de tous les deverser. Le panneau explique l'attente
			   (`criteres_requis`) au lieu de rester vide sans raison. */
			const incomplet = criteria.some((c) => values[c] === '');

			this.state.root.querySelectorAll(`[data-step="${step.cle}"] [data-addon-option]`).forEach((row) => {
				/* Un critere vide cote add-on reste un joker (les ampoules et
				   l'isolateur n'ont ni couleur ni finition). `getAttribute` et non
				   dataset : une cle a tirets (couleur-mecanisme) ne se laisse pas
				   reconstruire par simple capitalisation. */
				const match = !incomplet && criteria.every((c) => {
					const attendu = row.getAttribute('data-match-' + c);
					return !attendu || attendu === values[c];
				});
				row.hidden = !match;
			});

			/* Les add-ons choisis devenus invisibles sont retires. Plus aucun
			   choix -> "sans option" quand il existe ; une etape OBLIGATOIRE n'a
			   pas de bouton "sans" : le blocage panier (etape requise) reprend
			   la main. */
			const pressed = [...this.state.root.querySelectorAll(`[data-step="${step.cle}"] [data-opt="${step.cle}"][aria-pressed="true"]`)]
				.filter((b) => b.value);
			pressed.forEach((b) => {
				const row = b.closest(this.config.selectors.option);
				if (row && row.hidden) {
					b.setAttribute('aria-pressed', 'false');
					this._resetAddonQty(b);
				}
			});
			if (pressed.length && !pressed.some((b) => b.getAttribute('aria-pressed') === 'true')) {
				const sans = this.state.root.querySelector(`[data-step="${step.cle}"] [data-opt="${step.cle}"][value=""]`);
				if (sans) sans.setAttribute('aria-pressed', 'true');
			}

			/* Etape placee AVANT ses criteres (ex. la patere en panneau 2, appariee
			   sur la finition choisie au panneau 3) : le panneau serait vide sans
			   explication. Criteres remplis : aucun texte (la liste « Compatibles :
			   … » de recette a ete retiree, demande ADR du 2026-10-07). */
			const hintEl = this.state.root.querySelector(`[data-step="${step.cle}"] [data-addon-hint]`);
			if (hintEl) {
				const manquantes = criteria.filter((c) => values[c] === '')
					.map((c) => { const s = this.state.steps.find((x) => x.cle === c); return (s && s.libelle) || c; });
				hintEl.textContent = incomplet ? this._t('criteresRequis', { etapes: manquantes.join(', ') }) : '';
			}
		});
	},

	/**
	 * Résout la variante bundle : bundles[cléProduit][sousCléVariante] -> id.
	 * Clés dérivées des étapes product-selector / variant-option (rien en dur).
	 */
	_resolveBundleVariant() {
		/* Étape non terminée : une clé partielle ne résout rien, et ce n'est pas
		   une erreur — le parcours est simplement en cours. On compte les étapes
		   réellement remplies : un parcours à 1 sélecteur et 0 option de variante
		   reste résoluble (varKey '' -> entrée à clé vide générée par le Liquid). */
		if (!this._productSelectorsComplets() || !this._variantOptionsCompletes()) return null;

		const prodKey = this._productKey();

		const bundle = (this.state.data.bundles || {})[prodKey];
		if (!bundle) return null;
		const entry = bundle[this._variantKey(bundle)];
		if (!entry) return null;

		// choices = {cle: valeur} pour toutes les étapes de sélection visibles
		const choices = {};
		this._productSelectors.concat(this._visibleVariantOptions()).forEach((cle) => { choices[cle] = this._get(cle); });

		return {
			id: Number(entry.id),
			price: Number(entry.price) || 0,
			available: entry.available !== false,
			choices,
		};
	},

	/**
	 * Nombre d'unités commandées pour une étape quantity. La règle vient de la
	 * donnée (`n-plus-1`) : le client saisit n mètres, on commande n+1 unités.
	 * Centralisé ici — le hint et le panier doivent toujours dire la même chose.
	 */
	_units(step, n) {
		return step.rule === 'n-plus-1' ? n + 1 : n;
	},

	/**
	 * Lignes pop-up « Choisir mon modele » : quand une variante de la modale est
	 * choisie, le CTA de la ligne bascule sur « Supprimer l'option » (bouton
	 * moteur a valeur vide — le retrait retombe sur « sans option »).
	 */
	_refreshPopupRows() {
		this.state.root.querySelectorAll('[data-popup-row]').forEach((row) => {
			const dlg = this.state.root.querySelector(`[id="${row.dataset.popupRow}"]`);
			const chosen = dlg && dlg.querySelector('[data-opt][aria-pressed="true"]');
			const open = row.querySelector('[data-popup-open]');
			const remove = row.querySelector('[data-popup-remove]');
			if (open) open.hidden = !!chosen;
			if (remove) remove.hidden = !chosen;
		});
	},

	/**
	 * Retire la variante choisie dans la modale d'une ligne pop-up. Si l'etape
	 * n'a plus aucun accessoire choisi, « sans option » se re-presse.
	 */
	_removePopupChoice(btn) {
		const dlg = this.state.root.querySelector(`[id="${btn.dataset.popupRemove}"]`);
		if (!dlg) return;

		let cle = '';
		dlg.querySelectorAll('[data-opt][aria-pressed="true"]').forEach((b) => {
			cle = b.dataset.opt;
			b.setAttribute('aria-pressed', 'false');
		});

		if (cle) {
			const container = btn.closest('[data-role]');
			const encore = [...container.querySelectorAll(`[data-opt="${cle}"][aria-pressed="true"]`)].some((b) => b.value);
			const sans = container.querySelector(`[data-opt="${cle}"][value=""]`);
			if (!encore && sans) sans.setAttribute('aria-pressed', 'true');
		}
		this._refreshSummary();
	},

	_refreshSummary() {
		this._refreshTotalPrice();
		this._refreshPanelThumb();
		this._refreshSpecs();
		/* AVANT `_refreshStepValues` : c'est lui qui pose `--todo`, et le rappel
		   des choix se masque dessus. */
		this._refreshStepProgress();
		this._refreshStepValues();
		this._refreshStepDimensions();
		this._refreshPopupRows();
		this._refreshAvailability();
	},

	/**
	 * PRIX DU BUNDLE pour les choix deja faits, ou `null` si le produit n'est pas
	 * encore designe (le prix rendu par Liquid reste alors en place).
	 *
	 * Distinct de `_variantPartiel`, qui sert aux visuels : celui-ci teste la
	 * PRESENCE d'un prix (un nombre, pas un tableau) et accepte une cle variante
	 * vide — des le montage et la taille choisis, toutes les variantes du produit
	 * coutent le meme prix, il n'y a rien a attendre de plus.
	 */
	_prixBundlePartiel() {
		if (!this._productSelectorsComplets()) return null;

		const bundle = (this.state.data.bundles || {})[this._productKey()];
		if (!bundle) return null;

		const prixDe = (entree) => (entree && Number.isFinite(Number(entree.price)) ? Number(entree.price) : null);

		/* Variante exacte quand elle est connue. */
		const segments = this._variantKeySteps(bundle).map((cle) => this._get(cle));
		const exact = prixDe(bundle[segments.join('/')]);
		if (exact !== null) return exact;

		/* Sinon la premiere entree compatible avec les segments deja choisis, un
		   segment vide valant joker. Couvre aussi le parcours sans option de
		   variante, dont le Liquid genere une entree a cle vide. */
		for (const cle of Object.keys(bundle)) {
			if (cle.startsWith('_')) continue;
			const parts = cle.split('/');
			if (!segments.every((seg, i) => !seg || parts[i] === seg)) continue;
			const p = prixDe(bundle[cle]);
			if (p !== null) return p;
		}

		return null;
	},

	/**
	 * PRIX TOTAL : variante de bundle résolue + add-ons + cordon au mètre.
	 *
	 * Le prix s'affiche dès que le PRODUIT est désigné (montage + taille), sans
	 * attendre les options de variante : couleur, finition et fil ne changent pas
	 * le tarif. Attendre le parcours complet laissait le prix de la fiche
	 * d'accueil — souvent tres eloigne du reel — pendant tout le parcours, puis
	 * le faisait chuter d'un coup au dernier clic.
	 *
	 * Meme resolution partielle que les visuels (`_variantPartiel`) : variante
	 * exacte si elle est connue, sinon la premiere compatible avec les choix
	 * deja faits. Tant que le produit n'est pas designe, on laisse le prix rendu
	 * par Liquid.
	 */
	_refreshTotalPrice() {
		const form = this._form();
		if (!form) return;

		/* TOUS les noeuds de prix, pas seulement le premier : la fiche en a deux,
		   celui du bloc `price` (masque sous 768px) et celui de la barre sticky,
		   qui est le seul visible en mobile — d'ou le document. Une carte ne
		   touche qu'au prix de SA popup. */
		const scope = this._ownsProductPage() ? document : form;
		const wrappers = [...scope.querySelectorAll(this.config.selectors.priceNode)]
			/* Hors de tout formulaire (barre sticky) ou dans le sien, jamais dans
			   celui d'une carte voisine (recommandations de la fiche). */
			.filter((node) => {
				const owner = node.closest(this.config.selectors.form);
				return !owner || owner === form;
			});
		const targets = wrappers
			.map((node) => node.querySelector('.mm-product-price'))
			.filter(Boolean);
		if (!targets.length) return;

		const prixBundle = this._prixBundlePartiel();
		if (prixBundle === null) return;

		/* Le prix de la fiche d'accueil (0 €) est masque par le Liquid
		   (`data-price-pending`) : il apparait des que celui du bundle est connu. */
		wrappers.forEach((node) => node.removeAttribute('data-price-pending'));

		let total = prixBundle;

		this._addonSteps.forEach((step) => {
			this._getAddonsFor(step.cle).forEach((addon) => {
				total += addon.price * addon.quantity;
			});
		});

		this._quantitySteps.forEach((step) => {
			const n = this._metreValue(step);
			if (n <= 0) return;
			const unitCents = Number((this.state.data.cordon_metre || {}).price_cents) || 0;
			total += unitCents * this._units(step, n);
		});

		/* Meme multiplication qu'au panier : sans elle, 2 luminaires affichaient
		   le prix d'un seul avec ses accessoires. */
		total *= this._quantite();

		/* Le money_format du shop enveloppe le montant dans un <span class=wd_discount> :
		   on ecrit dedans quand il existe, sinon `textContent` sur le parent
		   supprimerait ce span et le prix perdrait sa mise en forme. */
		const montant = this._formatMoney(total);
		targets.forEach((target) => {
			const node = target.querySelector('.wd_discount') || target;
			node.textContent = montant;
		});
	},

	/**
	 * Valeur saisie d'une étape quantity (0 si l'étape est masquée).
	 */
	_metreValue(step) {
		const field = this.state.root.querySelector(`[data-step="${step.cle}"]`);
		if (!field || field.hidden) return 0;
		const input = field.querySelector('[data-metre-input]');
		const total = parseInt((input || {}).value || '0', 10) || 0;
		/* L'affichage porte la longueur TOTALE (« 3 m »), longueur standard
		   comprise (Figma 2849:162901) ; le moteur, lui, ne commande que le
		   supplement. */
		return Math.max(0, total - this._metreBase(field));
	},

	/**
	 * Longueur standard incluse dans le bundle, annoncee par le Liquid.
	 */
	_metreBase(field) {
		const wrap = field.querySelector('[data-metre-stepper]');
		return parseInt((wrap || {}).dataset?.metreBase || '0', 10) || 0;
	},

	_getAddonsFor(cle) {
		/* Etape masquee par visible_si (ex. la patere apres bascule C -> A) : sa
		   selection memorisee ne part ni au panier ni dans le prix total. */
		if (!this._stepVisible(cle)) return [];

		/* Etape optionnelle = selection multiple : chaque bouton presse a valeur
		   non vide est un accessoire du bundle. Etape obligatoire : un seul. */
		return [...this.state.root.querySelectorAll(`[data-step="${cle}"] [data-opt="${cle}"][aria-pressed="true"]`)]
			.filter((el) => el.value)
			.map((el) => {
				/* Quantité propre à la ligne quand l'add-on est quantifiable ; 1 sinon. */
				const qtyInput = el.closest(this.config.selectors.option)?.querySelector('[data-addon-qty]');
				const quantity = Math.max(1, parseInt((qtyInput || {}).value || '1', 10) || 1);
				return {
					id: Number(el.value),
					title: el.dataset.title || '',
					price: Number(el.dataset.price) || 0,
					quantity,
				};
			});
	},

	_groupId() {
		/* randomUUID : deux configurateurs ouverts dans deux onglets pouvaient
		   produire le même identifiant avec performance.now(). */
		if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
		this._seq = (this._seq || 0) + 1;
		return `mmb-${Date.now()}-${this._seq}`;
	},

	/**
	 * Etape addon-matched REQUISE encore sans choix : visible (ni son fieldset
	 * ni son accordeon masques par visible_si), non optionnelle, valeur vide.
	 * Ex. la patere pour applique, obligatoire sur le montage C.
	 */
	_requiredStepMissing() {
		return this._addonSteps.find((step) => {
			if (step.optionnelle === true) return false;
			if (!this._stepVisible(step.cle)) return false;
			return this._get(step.cle) === '';
		});
	},

	/**
	 * Lignes panier : bundle + add-ons (matched) + quantités (règle dérivée).
	 * Boucle sur steps[] — structure _mm_bundle_* inchangée.
	 */
	_buildItems() {
		const manquante = this._requiredStepMissing();
		if (manquante) return { error: 'step-required', etape: manquante.libelle || manquante.cle };

		const v = this._resolveBundleVariant();
		if (!v) return { error: 'variant-not-found' };
		/* Variante epuisee : Shopify refuserait l'ajout (422) et le client ne
		   verrait que « Erreur lors de l'ajout ». Le CTA est deja desactive
		   (`_refreshCta`), ce garde couvre un submit qui passerait quand meme. */
		if (!v.available) return { error: 'unavailable' };

		const group = this._groupId();

		/* Quantité choisie sur la fiche produit : elle multiplie le luminaire ET
		   ses accessoires (deux luminaires = deux patères, deux longueurs de fil),
		   comme la personnalisation produit. Le panier garde ensuite ce ratio au
		   +/- (mm_cart.js, `_groupUpdates`). */
		const quantity = this._quantite();

		/* Propriétés de commande : noms et valeurs viennent des data-* de l'option
		   choisie, jamais du textContent — elles finissent sur la commande client.
		   Une propriété PAR ACCORDÉON, nommée par son titre et listant ses choix
		   dans l'ordre du parcours (« Abat-jour : Grand modèle, Blanc, Emaillée »),
		   comme le récap panier Figma. Repli sur la légende d'étape hors accordéon. */
		const bundleProps = {};
		const cles = this._productSelectors.concat(this._visibleVariantOptions());
		this.state.steps.map((s) => s.cle).filter((cle) => cles.includes(cle)).forEach((cle) => {
			const label = this._optionLabel(cle);
			const name = this._stepGroupName(cle) || this._stepLegend(cle);
			if (!name || !label) return;
			bundleProps[name] = bundleProps[name] ? `${bundleProps[name]}, ${label}` : label;
		});
		bundleProps._mm_bundle_group = group;
		bundleProps._mm_bundle_parent = group;

		const items = [{ id: v.id, quantity, properties: bundleProps }];

		// Add-ons matched (selection multiple sur les etapes optionnelles)
		this._addonSteps.forEach((step) => {
			this._getAddonsFor(step.cle).forEach((addon) => {
				items.push({ id: addon.id, quantity: addon.quantity * quantity, properties: { _mm_bundle_group: group, _mm_bundle_child: group, _mm_addon_label: addon.title } });
			});
		});

		// Quantités (règle n / n-plus-1)
		this._quantitySteps.forEach((step) => {
			const n = this._metreValue(step);
			if (n <= 0) return;
			const units = this._units(step, n);
			const filCle = step.variante_par;
			const fil = filCle ? this._get(filCle) : '';
			const variants = (this.state.data.cordon_metre || {}).variants || {};
			const qtyId = variants[fil];
			if (!qtyId) return;
			const label = this._t('metreLabel', { fil: fil, n: n });
			items.push({ id: Number(qtyId), quantity: units * quantity, properties: { _mm_bundle_group: group, _mm_bundle_child: group, _mm_addon_label: label } });
		});

		return { items };
	},

	async _handleAdd(btn) {
		const feedback = this.state.root.querySelector(this.config.selectors.feedback);
		const { items, error, etape } = this._buildItems();
		if (error) {
			if (feedback) {
				feedback.textContent = error === 'step-required'
					? this._t('etapeRequise', { etape: etape })
					: this._t('unavailable');
			}
			return;
		}
		/* Le CTA vient du formulaire produit : il peut manquer (variante epuisee,
		   ou rendu sticky uniquement). L'ajout ne doit pas en dependre. */
		if (btn) {
			btn.disabled = true;
			btn.setAttribute('aria-busy', 'true');
			btn.classList.add(this.config.classes.loading);
		}
		if (feedback) feedback.textContent = '';
		try {
			await this._addMultiple(items);
			if (feedback) feedback.textContent = this._t('added');
			if (typeof window.mmOpenCart === 'function') window.mmOpenCart();
		} catch (e) {
			if (feedback) feedback.textContent = this._t('error');
			window.log('error', '🔧 [CONFIGURATEUR]', 'Add failed', { error: e.message });
		} finally {
			if (btn) {
				btn.disabled = false;
				btn.removeAttribute('aria-busy');
				btn.classList.remove(this.config.classes.loading);
			}
		}
	},

	/**
	 * Ajout au panier. `CartAPI.request` porte deja le timeout, la mise a jour du
	 * compteur et le refresh du drawer (avec re-montage Splide) : le configurateur
	 * refaisait tout ca a la main, en perdant le compteur au passage.
	 */
	async _addMultiple(items) {
		/* Section du tiroir panier demandee avec l'ajout, comme le flux natif
		   (mm_cart.js) : sans elle, `CartAPI.updateUI` n'a rien a re-rendre et le
		   tiroir s'ouvrait sur « Votre panier est vide ». */
		const cart = document.querySelector('[data-cart], .mm-cart');
		const sectionId = cart && cart.id ? cart.id.replace('shopify-section-', '') : '';
		const body = sectionId ? { items, sections: sectionId } : { items };

		// shopify:cart:lines-update au DEBUT de l'ajout (spec), promise reglee sur tous les chemins
		const standardEvent = window.mmStandardEvents?.linesUpdate(this.state.root, {
			action: 'add',
			context: 'product',
			lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
		});
		try {
			// mm_cart.js est charge a la demande (PERF-15) : l'attendre avant CartAPI.
			await window.mmLoadCart?.();
			const data = await window.CartAPI.request('POST', 'cart/add.js', body);
			// cart/add.js ne renvoie que les lignes ajoutees : le panier complet est relu
			standardEvent?.resolve();
			// Evenement interne, meme nom que le flux natif (mm_cart.js)
			window.emit('cart:add-to-cart-success', { response: data, items });
			return data;
		} catch (error) {
			standardEvent?.fail(error);
			throw error;
		}
	},

	/**
	 * Formatage monetaire derive du prix DEJA rendu par Liquid : ses separateurs
	 * et son symbole sont, par construction, ceux du money_format du shop.
	 * Reconstruire le format avec Intl echouait deux fois (2 decimales forcees,
	 * espace insecable au lieu du point) — cf. mmUpdatePrice dans mm_main_product.
	 */
	_formatMoney(cents) {
		const tpl = this._moneyTemplate();
		const amount = cents / 100;
		const hasCents = Math.round(cents) % 100 !== 0;
		const fixed = amount.toFixed(hasCents ? 2 : 0);
		let [intPart, decPart] = fixed.split('.');
		if (tpl.groupSep) {
			intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, tpl.groupSep);
		}
		return tpl.prefix + intPart + (decPart ? tpl.decimalSep + decPart : '') + tpl.suffix;
	},

	/**
	 * Prefixe / suffixe / separateurs, lus une fois sur `data-money-sample`
	 * (un 1234,56 passe par le meme filtre Liquid, donc porteur du separateur
	 * de milliers meme quand le prix affiche fait moins de 4 chiffres).
	 */
	_moneyTemplate() {
		if (this._moneyTpl) return this._moneyTpl;

		const node = document.querySelector(this.config.selectors.priceNode);

		/* Le money_format du shop peut contenir du markup (<span class=wd_discount>) :
		   on lit le TEXTE rendu, sinon prefixe et suffixe embarqueraient les balises. */
		let sample = node && node.dataset.moneySample;
		if (sample && sample.indexOf('<') !== -1) {
			const tmp = document.createElement('div');
			tmp.innerHTML = sample;
			sample = tmp.textContent;
		}

		const fallback = { prefix: '', suffix: ' €', decimalSep: ',', groupSep: '.' };

		if (!sample) { this._moneyTpl = fallback; return this._moneyTpl; }

		const match = sample.match(/[\d][\d\s.,  ]*\d|\d/);
		if (!match) { this._moneyTpl = fallback; return this._moneyTpl; }

		const rendered = match[0];
		const decimalMatch = rendered.match(/([.,])(\d{1,2})$/);
		this._moneyTpl = {
			prefix: sample.slice(0, match.index),
			suffix: sample.slice(match.index + rendered.length),
			decimalSep: decimalMatch ? decimalMatch[1] : ',',
			groupSep: (rendered.match(/[\s.,  ](?=\d{3}\b)/) || [])[0] || '',
		};
		return this._moneyTpl;
	},

};

window.ConfigurateurManager = ConfigurateurManager;

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => ConfigurateurManager.init());
} else {
	ConfigurateurManager.init();
}
document.addEventListener('shopify:section:load', () => ConfigurateurManager.init());
})();
